/**
 * AdminPhotosPage - Simple photo management admin page
 * Upload, view, and delete gallery photos from S3
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/AdminPhotosPage.css';
import {
  uploadPhotoToS3,
  deletePhotoFromS3,
  getPhotoUrl,
  getThumbnailUrl
} from '../services/s3Service';
import { getAllPhotosWithUrls } from '../services/photoMetadataService';
import { PhotoUploader } from '../components/PhotoManager';

// Gate debug logging behind NODE_ENV for development use only
const debugLog = (...args: any[]) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(...args);
  }
};

debugLog('[DEBUG AdminPhotosPage] Component loaded');

// Simple photo type for display
interface S3Photo {
  photoId: string;
  s3Key: string;
  url: string;
  thumbnailUrl: string;
  filename: string;
}

const AdminPhotosPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  // State
  const [photos, setPhotos] = useState<S3Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Delete state
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // Notification state
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  debugLog('[DEBUG AdminPhotosPage] Render, photos:', photos.length, 'loading:', loading);

  // Note: Authentication is handled by ProtectedRoute wrapper in App.tsx
  // No need for manual auth check here

  // Fetch photos on mount
  useEffect(() => {
    fetchPhotos();
  }, []);

  // Fetch photos from API (with metadata from DynamoDB)
  const fetchPhotos = async () => {
    debugLog('[DEBUG AdminPhotosPage] Fetching photos from API');
    setLoading(true);
    setError(null);

    try {
      const photosFromApi = await getAllPhotosWithUrls(true); // Include hidden for admin
      debugLog('[DEBUG AdminPhotosPage] Got photos:', photosFromApi.length);

      const photosData: S3Photo[] = photosFromApi.map(photo => ({
        photoId: photo.photoId,
        s3Key: photo.s3Key,
        url: photo.url,
        thumbnailUrl: photo.thumbnailUrl,
        filename: photo.filename
      }));

      setPhotos(photosData);
      debugLog('[DEBUG AdminPhotosPage] Loaded', photosData.length, 'photos');
    } catch (err) {
      console.error('[DEBUG AdminPhotosPage] Error fetching photos:', err);
      setError('Failed to load photos. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show notification
  const showNotification = useCallback((type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  }, []);

  // Handle logout
  const handleLogout = async () => {
    debugLog('[DEBUG AdminPhotosPage] Logging out...');
    try {
      await logout();
      debugLog('[DEBUG AdminPhotosPage] Logout successful, navigating to login');
      navigate('/admin');
    } catch (error) {
      console.error('[DEBUG AdminPhotosPage] Logout error:', error);
      // Navigate anyway on error
      navigate('/admin');
    }
  };

  // Handle upload start
  const handleUploadStart = () => {
    debugLog('[DEBUG AdminPhotosPage] Upload started');
    setUploading(true);
    setUploadProgress(0);
  };

  // Handle upload progress
  const handleUploadProgress = (progress: number) => {
    debugLog('[DEBUG AdminPhotosPage] Upload progress:', progress);
    setUploadProgress(progress);
  };

  // Handle upload complete (for a single file)
  const handleUploadComplete = async (file: File) => {
    debugLog('[DEBUG AdminPhotosPage] Uploading file:', file.name);

    try {
      // Upload to S3
      const uploadResult = await uploadPhotoToS3(file);

      if (!uploadResult.success || !uploadResult.s3Key) {
        throw new Error(uploadResult.error || 'Upload failed');
      }

      debugLog('[DEBUG AdminPhotosPage] S3 upload successful:', uploadResult.s3Key);

      // Refresh photos list
      await fetchPhotos();

      showNotification('success', `Photo "${file.name}" uploaded successfully!`);
    } catch (err) {
      console.error('[DEBUG AdminPhotosPage] Upload error:', err);
      throw err;
    }
  };

  // Handle upload error
  const handleUploadError = (error: string) => {
    console.error('[DEBUG AdminPhotosPage] Upload error:', error);
    setUploading(false);
    showNotification('error', error);
  };

  // Handle delete click - show confirmation (now uses photoId)
  const handleDeleteClick = (photoId: string) => {
    debugLog('[DEBUG AdminPhotosPage] Delete clicked:', photoId);
    setConfirmDelete(photoId);
  };

  // Handle delete confirm
  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;

    debugLog('[DEBUG AdminPhotosPage] Confirming delete for photoId:', confirmDelete);
    setDeletingKey(confirmDelete);

    try {
      const deleteResult = await deletePhotoFromS3(confirmDelete);

      if (!deleteResult.success) {
        throw new Error(deleteResult.error || 'Delete failed');
      }

      // Update local state
      setPhotos(prev => prev.filter(p => p.photoId !== confirmDelete));
      showNotification('success', 'Photo deleted successfully!');
    } catch (err) {
      console.error('[DEBUG AdminPhotosPage] Error deleting:', err);
      showNotification('error', 'Failed to delete photo');
    } finally {
      setDeletingKey(null);
      setConfirmDelete(null);
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setConfirmDelete(null);
  };

  return (
    <div className="admin-photos-page">
      <div className="container">
        {/* Header */}
        <header className="admin-header">
          {/* Admin Navigation */}
          <nav className="admin-nav">
            <NavLink
              to="/admin/reviews"
              className={({ isActive }) => `admin-nav-link ${isActive ? 'active' : ''}`}
            >
              Reviews
            </NavLink>
            <NavLink
              to="/admin/photos"
              className={({ isActive }) => `admin-nav-link ${isActive ? 'active' : ''}`}
            >
              Photos
            </NavLink>
          </nav>

          <h1>Photo Manager</h1>
          <p>Upload and manage gallery photos.</p>
          <button className="logout-button" onClick={handleLogout}>Logout</button>
        </header>

        {/* Notification */}
        {notification && (
          <div className={`notification ${notification.type}`}>
            {notification.message}
            <button onClick={() => setNotification(null)}>&times;</button>
          </div>
        )}

        {/* Stats Bar */}
        <div className="photos-stats">
          <div className="stat-item">
            <span className="stat-value">{photos.length}</span>
            <span className="stat-label">Total Photos</span>
          </div>
        </div>

        {/* Upload Section */}
        <div className="upload-section">
          <h2>Upload Photos</h2>
          <PhotoUploader
            onUploadStart={handleUploadStart}
            onUploadProgress={handleUploadProgress}
            onUploadComplete={handleUploadComplete}
            onUploadError={handleUploadError}
            disabled={loading}
            uploading={uploading}
          />
          {uploadProgress > 0 && uploadProgress < 100 && (
            <div className="upload-progress-bar">
              <div
                className="upload-progress-fill"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}
        </div>

        {/* Photos Grid */}
        <div className="photos-section">
          <h2>Gallery Photos ({photos.length})</h2>
          {loading ? (
            <div className="loading-message">Loading photos...</div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : photos.length === 0 ? (
            <div className="empty-message">No photos yet. Upload some photos to get started!</div>
          ) : (
            <div className="simple-photo-grid">
              {photos.map(photo => (
                <div key={photo.s3Key} className="simple-photo-card">
                  <div className="photo-image-container">
                    <img
                      src={photo.thumbnailUrl}
                      alt={photo.filename}
                      onError={(e) => {
                        // Fallback to full image if thumbnail doesn't exist
                        (e.target as HTMLImageElement).src = photo.url;
                      }}
                    />
                  </div>
                  <div className="photo-info">
                    <span className="photo-filename" title={photo.filename}>
                      {photo.filename}
                    </span>
                    <button
                      className="delete-button"
                      onClick={() => handleDeleteClick(photo.photoId)}
                      disabled={deletingKey === photo.photoId}
                    >
                      {deletingKey === photo.photoId ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {confirmDelete && (
          <div className="modal-overlay" onClick={handleDeleteCancel}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <h3>Delete Photo?</h3>
              <p>Are you sure you want to delete this photo? This action cannot be undone.</p>
              <div className="modal-preview">
                <img
                  src={photos.find(p => p.photoId === confirmDelete)?.thumbnailUrl}
                  alt="Photo to delete"
                  onError={(e) => {
                    const photo = photos.find(p => p.photoId === confirmDelete);
                    if (photo) {
                      (e.target as HTMLImageElement).src = photo.url;
                    }
                  }}
                />
              </div>
              <div className="modal-buttons">
                <button className="cancel-button" onClick={handleDeleteCancel}>
                  Cancel
                </button>
                <button className="delete-confirm-button" onClick={handleDeleteConfirm}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPhotosPage;
